// ═══════════════════════════════════════════════════════════════════════════
// Per-action translators — LAYER-MANAGEMENT domain (T7, agent-bridge)
//                                                          — PACKAGE-INTERNAL
// ═══════════════════════════════════════════════════════════════════════════
//
// The thin per-action translators behind the LAYER-management domain
// (CREATE_LAYER / RENAME_LAYER / SET_ACTIVE_LAYER). Each AI shape maps 1:1
// onto the corresponding contract `EditorCommand`.
//
// IMPORTANT (AI safe contour): these translators produce EditorCommands
// WITHOUT running the CC-2a fail-closed guard (`assertAiCallableLayerAction`).
// They are therefore PACKAGE-INTERNAL: this file is NOT re-exported by the
// barrel (`index.ts`). The ONLY public LAYER entry is the guarded
// `translateLayerCommand` in `layerDispatcher.ts` (which imports these). This
// mirrors every other domain (BLOCK / ENTITY / DRAW-MODIFY / VIEW-SPACE /
// MATCHPROP / BASE-PRIM) — a consumer of `@dxf-ai-editor/ai-commands` can
// never emit `header/add-layer` etc. past the guard. Honours the CLAUDE.md
// rules: "AI drafting … может только предлагать и исполнять команды через
// безопасный контур." + "любые изменения геометрии только через Command
// Engine."

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import { layerDefinitionSchema } from "@dxf-ai-editor/drawing-model";

import { AI_COMMAND_SOURCE } from "./blockDispatcherShared";
import type {
  CreateLayerAiCommand,
  RenameLayerAiCommand,
  SetActiveLayerAiCommand,
} from "./layerSchemas";

// ─── CREATE_LAYER → header/add-layer ────────────────────────────────────────
//
// The reducer (`header/add-layer` → `appendStyleTableEntry`,
// styleTablesReducer.ts) appends `command.layer` VERBATIM — it does NOT fill
// Zod defaults in PRODUCTION (`assembleDrawingDocument`'s PROD branch calls
// `validateDrawingModel`, which checks only entity-id/spaceId/activeSpaceId
// uniqueness, and returns the document UNCHANGED — see model.ts). A raw
// `{ name }` payload would therefore ship with `color`/`visible`/`frozen`/…
// all `undefined` in production (a real rendering bug), unlike DEV/test where
// the Zod re-parse happens to paper over it. So — UNLIKE most translators in
// this package — this one is NOT a bare 1:1 field copy: it runs the payload
// through `layerDefinitionSchema.parse(...)` HERE, at translate time, so the
// EMITTED command already carries the FULLY-RESOLVED record (color 7,
// Continuous, lineweight Default, visible/plottable, …) — byte-identical to
// what the human-facing `addLayer` wrapper hand-constructs
// (editorStoreStyleTables.ts). One truth for "what a brand-new AutoCAD LAYER-
// New layer looks like" (the schema's own `.default(...)` values), not a
// second hand-copied literal.
export function translateCreateLayer(
  command: CreateLayerAiCommand,
): EditorCommand[] {
  return [
    {
      type: "header/add-layer",
      source: AI_COMMAND_SOURCE,
      layer: layerDefinitionSchema.parse({ name: command.name }),
    },
  ];
}

// ─── RENAME_LAYER → header/rename-layer ─────────────────────────────────────
export function translateRenameLayer(
  command: RenameLayerAiCommand,
): EditorCommand[] {
  return [
    {
      type: "header/rename-layer",
      source: AI_COMMAND_SOURCE,
      oldName: command.oldName,
      newName: command.newName,
    },
  ];
}

// ─── SET_ACTIVE_LAYER → properties/apply (empty selection) ──────────────────
//
// Moves the CURRENT-layer pointer (CLAYER) by dispatching `properties/apply`
// with an EMPTY `entityIds` — the SAME "no-selection" mechanic
// `setCurrentLayer` / `setCurrentDimensionStyle` / SET_CANNOSCALE's sibling
// current-pointer changes use (propertyReducer.ts's `entityIds.length === 0`
// branch merges `values` into `state.propertyWorkflows.defaultObjectProperties`
// generically — it is NOT the CHPROP/PROPERTIES per-SELECTED-entity edit path
// ТЗ §10.1 gates; that path is `entityIds.length > 0`). This is why
// `assertAiCallableLayerAction` guards on the "LAYER" registry id, never on
// "PROPERTIES" — see `layerGuard.ts`'s module comment and the general
// `aiCallableGuard.ts` precedent for internal-mechanic literals.
//
// THIN / TOTAL (no existence validation) — deliberately mirrors
// `translateSetCannoscale` (blockDispatcherDynamic.ts), NOT the human-facing
// `setCurrentLayer` store wrapper (which validates against the live document,
// since the AI translator receives no live-document context here, matching
// every other "current pointer" AI action in this package). An unknown name
// is a low-blast-radius, self-correcting condition (the NEXT T1 `layers`
// read exposes it; the agent can re-issue SET_ACTIVE_LAYER) — not a data-
// corruption risk, since no entity is touched.
export function translateSetActiveLayer(
  command: SetActiveLayerAiCommand,
): EditorCommand[] {
  return [
    {
      type: "properties/apply",
      source: AI_COMMAND_SOURCE,
      entityIds: [],
      values: { Layer: command.name },
    },
  ];
}
